import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Award,
  ExternalLink,
  Package,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Search,
  ShieldCheck,
  ShieldPlus,
  FileText,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Settings,
  TrendingDown,
  Truck,
} from 'lucide-react'
import { ManufacturerCompetitorPrices } from '@/components/manufacturer-competitor-prices'
import { MapPolicyTab } from '@/components/map-policy-tab'
import { SourcingPanel, type SourceSheet } from '@/features/manufacturers/sourcing-panel'
import { ManufacturerGlance } from '@/features/manufacturers/manufacturer-glance'
import { RetirePanel } from '@/features/manufacturers/retire-panel'
import { StoreVisibilityPanel } from '@/features/manufacturers/store-visibility-panel'
import { SettingCell } from '@/components/setting-cell'
import { ManualProductDialog } from '@/features/manufacturers/manual-product-dialog'
import type { ManualProduct } from '@/api/manual-products'
import type { components } from '@/api/schema'
import { TrackingPanel } from '@/features/manufacturers/tracking-panel'
import { CallsPanel } from '@/features/phone-calls/calls-panel'
import { QualityTiersPanel } from '@/features/manufacturers/quality-tiers-panel'
import { QualityTierSummary } from '@/features/manufacturers/quality-tier-summary'
import { SafewarePlansPanel } from '@/features/manufacturers/safeware-plans-panel'
import { AddressSheet } from '@/features/manufacturers/address-sheet'
import { BackButton } from '@/components/back-button'
import { ManufacturerEmailsTab } from '@/features/manufacturer-data/emails-tab'
import { useManufacturer, useProducts, useUpdateManufacturer } from '@/api/hooks'
import { Switch } from '@/components/ui/switch'
import { ChoicePicker } from '@/components/choice-picker'
import { StatusPill } from '@/components/status-pill'
import { toast } from 'sonner'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { cn, formatDateOnly, formatMoney, formatStatus, parseUTCDate } from '@/lib/utils'
import type { ApInvoiceListItem } from '@/api/invoices'
import { formatInvoicePriority, formatInvoiceStatus } from '@/lib/status'
import { usePageTitle } from '@/lib/page-title'
import { PhoneNumber } from '@/components/phone-number'
import { SearchInput } from '@/components/search-input'

type Manufacturer = components['schemas']['ManufacturerDetailRead']

interface ManufacturerDetailPageProps {
  manuId: string
  /** The tab a link opened, e.g. ``emails`` from an action item (USA-1474). */
  tab?: string
  messageId?: number
  threadUuid?: string
  actionUuid?: string
}

function asApInvoices(value: unknown): ApInvoiceListItem[] {
  return Array.isArray(value) ? (value as ApInvoiceListItem[]) : []
}

function invoicePoSummary(invoice: ApInvoiceListItem) {
  const link = invoice.po_links?.find(
    (poLink) => poLink.purchase_order?.uuid || poLink.po_number_raw,
  )
  return {
    uuid: link?.purchase_order?.uuid || link?.purchase_order_uuid,
    number: link?.purchase_order?.po_number || link?.po_number_raw,
  }
}

function RelatedApInvoicesPanel({ invoices }: { invoices: ApInvoiceListItem[] }) {
  return (
    <TabsContent value="ap-invoices" className="mt-4">
      {!invoices.length ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No related AP invoices found for this manufacturer.
        </div>
      ) : (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>PO / project</TableHead>
                  <TableHead className="text-right">Amount / due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => {
                  const po = invoicePoSummary(invoice)
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell className="align-top">
                        <Link
                          to="/invoices/$invoiceId"
                          params={{ invoiceId: invoice.uuid! }}
                          className="font-mono font-medium text-primary hover:underline"
                        >
                          {invoice.invoice_number || 'Missing invoice #'}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          {invoice.vendor?.name || invoice.vendor_raw || 'Vendor pending'} ·{' '}
                          {formatDateOnly(invoice.invoice_date)}
                        </p>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="space-y-1 text-xs">
                          <Badge variant="outline">{formatInvoiceStatus(invoice.status)}</Badge>
                          <p className="text-muted-foreground">
                            {formatInvoicePriority(invoice.priority_status)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        {po.uuid ? (
                          <Link
                            to="/purchase-orders/$poId"
                            params={{ poId: po.uuid! }}
                            className="block font-mono text-primary hover:underline"
                          >
                            {po.number}
                          </Link>
                        ) : po.number ? (
                          <span className="font-mono">{po.number}</span>
                        ) : (
                          <span className="text-muted-foreground">No linked PO</span>
                        )}
                        {invoice.internal_project ? (
                          <Link
                            to="/projects/$projectId"
                            params={{ projectId: invoice.internal_project.uuid! }}
                            className="block text-xs text-primary hover:underline"
                          >
                            {invoice.internal_project.name}
                          </Link>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {invoice.project_ship_to_raw || 'Project pending'}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right align-top tabular-nums">
                        <p className="font-medium">{formatMoney(invoice.invoice_total)}</p>
                        <p className="text-xs text-muted-foreground">
                          Due{' '}
                          {formatDateOnly(invoice.official_due_date || invoice.priority_due_date)}
                        </p>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
          <div className="space-y-2 md:hidden">
            {invoices.map((invoice) => (
              <Link
                key={invoice.id}
                to="/invoices/$invoiceId"
                params={{ invoiceId: invoice.uuid! }}
                className="block rounded-lg border bg-card p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm font-medium text-primary">
                      {invoice.invoice_number || 'Missing invoice #'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {invoice.internal_project?.name ||
                        invoice.project_ship_to_raw ||
                        'Project pending'}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold">{formatMoney(invoice.invoice_total)}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {formatDateOnly(invoice.official_due_date || invoice.priority_due_date)}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[10px]">
                    {formatInvoiceStatus(invoice.status)}
                  </Badge>
                  <Badge variant="secondary" className="text-[10px]">
                    {formatInvoicePriority(invoice.priority_status)}
                  </Badge>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </TabsContent>
  )
}

function getImageUrl(
  images?: Array<{ hidden?: boolean; public_url?: string | null; store_url?: string | null }>,
) {
  const img = images?.find((i) => !i.hidden && (i.public_url || i.store_url))
  return img?.public_url || img?.store_url || null
}

function websiteHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/** Whether this manufacturer's products ship; off for warranties and services, whose POs carry no freight. */
function ShipsProductsSetting({ manuId, shipsProducts }: { manuId: string; shipsProducts: boolean }) {
  const update = useUpdateManufacturer(manuId)
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <Switch
        id="manufacturer-ships-products"
        checked={shipsProducts}
        disabled={update.isPending}
        onCheckedChange={(checked) =>
          update.mutate(
            { ships_products: checked },
            { onError: () => toast.error('Could not save the shipping setting') },
          )
        }
      />
      {!shipsProducts && (
        <span className="text-muted-foreground">
          Nothing ships, so its POs carry no freight (warranties, services).
        </span>
      )}
    </div>
  )
}

const PARCEL_FREIGHT_CHOICES = [
  { value: 'use_our_ups_account', label: 'Our UPS account' },
  { value: 'usars_books_freight', label: 'Our prepaid labels' },
  { value: 'manufacturer_books_freight', label: 'They ship and bill' },
] as const

type ParcelFreight = (typeof PARCEL_FREIGHT_CHOICES)[number]['value']

/** How this manufacturer's parcel orders ship (USA-1444): UPS on our account unless they can't. */
function ParcelFreightSetting({ manuId, parcelFreight }: { manuId: string; parcelFreight: ParcelFreight }) {
  const update = useUpdateManufacturer(manuId)
  const current = PARCEL_FREIGHT_CHOICES.find((choice) => choice.value === parcelFreight) ?? PARCEL_FREIGHT_CHOICES[0]
  return (
    <ChoicePicker
      title="Parcel orders ship on"
      label={`Parcel orders: ${current.label}. Change`}
      value={current.value}
      choices={PARCEL_FREIGHT_CHOICES}
      pending={update.isPending}
      onSelect={(value) =>
        update.mutate(
          { parcel_freight: value as ParcelFreight },
          { onError: () => toast.error('Could not save how their parcels ship') },
        )
      }
    >
      <StatusPill tone="neutral">{current.label}</StatusPill>
    </ChoicePicker>
  )
}

/** The manufacturer's other names, numbers and settings, kept off the overview (USA-1271). */
function ManufacturerDetails({ mfr, aliases }: { mfr: Manufacturer; aliases: string[] }) {
  const shipsProducts = mfr.ships_products ?? true
  const facts = [
    { label: 'Also known as', value: aliases.join(' · ') },
    { label: 'Toll free', value: mfr.toll_free },
    { label: 'Fax', value: mfr.fax },
    {
      label: 'MAP multiplier',
      value: mfr.list_price_map_multiplier ? String(mfr.list_price_map_multiplier) : null,
    },
  ]
  return (
    <section aria-labelledby="mfr-details" className="overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-4 py-2">
        <h2 id="mfr-details" className="text-sm font-semibold">
          Details
        </h2>
      </div>
      <div
        className={cn(
          '-mb-px -mr-px grid grid-cols-1 sm:grid-cols-2',
          shipsProducts ? 'lg:grid-cols-6' : 'lg:grid-cols-5',
        )}
      >
        {facts.map((fact) => (
          <SettingCell key={fact.label} label={fact.label}>
            <span className={fact.value ? 'text-sm' : 'text-sm text-muted-foreground'}>
              {fact.value || 'None'}
            </span>
          </SettingCell>
        ))}
        <SettingCell label="Ships products" htmlFor="manufacturer-ships-products">
          <ShipsProductsSetting manuId={mfr.uuid} shipsProducts={mfr.ships_products ?? true} />
        </SettingCell>
        {shipsProducts && (
          <SettingCell label="Parcel orders">
            <ParcelFreightSetting manuId={mfr.uuid} parcelFreight={mfr.parcel_freight ?? 'use_our_ups_account'} />
          </SettingCell>
        )}
      </div>
    </section>
  )
}

export function ManufacturerDetailPage({
  manuId,
  tab: linkedTab,
  messageId,
  threadUuid,
  actionUuid,
}: ManufacturerDetailPageProps) {
  const { data: mfr, isLoading } = useManufacturer(manuId)
  usePageTitle(mfr?.name)

  const [addressOpen, setAddressOpen] = useState(false)
  const [productsPage, setProductsPage] = useState(1)
  const [productSearch, setProductSearch] = useState('')
  const debouncedProductSearch = useDebouncedValue(productSearch)
  // A product we define being added ('new') or edited (USA-1108).
  const [editingProduct, setEditingProduct] = useState<ManualProduct | 'new' | null>(null)
  // Controlled so the header's ratings summary can open its tab (USA-1258). A tab picked by hand holds
  // until a link opens another one (USA-1474).
  const [picked, setPicked] = useState<{ tab: string; linked?: string } | null>(null)
  const tab = picked && picked.linked === linkedTab ? picked.tab : (linkedTab ?? null)
  const setTab = (next: string) => setPicked({ tab: next, linked: linkedTab })
  const tabsRef = useRef<HTMLDivElement>(null)
  // The overview's "All terms" opens the preferred source's sheet on the Sourcing tab.
  const [sourceSheet, setSourceSheet] = useState<SourceSheet>(null)

  const { data: productsData, isLoading: productsLoading } = useProducts({
    manufacturer: mfr?.name,
    search: debouncedProductSearch || undefined,
    page: productsPage,
    limit: 20,
  })

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 pb-24">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96 max-w-full" />
        <Skeleton className="h-44" />
      </div>
    )
  }

  if (!mfr) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Manufacturer not found</p>
      </div>
    )
  }

  const hasShopify = !!mfr.shopify_collection_id
  const contactLinks = [...(mfr.contact_links ?? [])].sort((a, b) => a.id - b.id)
  const relatedApInvoices = asApInvoices(mfr.ap_invoices)
  const address = [
    mfr.address1,
    mfr.address2,
    [mfr.city, [mfr.state, mfr.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ')
  const aliases = [mfr.short_name !== mfr.name ? mfr.short_name : null, mfr.acronym].filter(
    (alias): alias is string => !!alias,
  )
  // AutoQuotes supplies an AQ manufacturer's products; staff define everyone else's.
  const definesProducts = mfr.source !== 'auto_quotes_api'
  // The overview's "All terms" and the Emails tab's lead-time check open the preferred source's sheet.
  const openPreferredSource = () => {
    const preferred = [...(mfr.sourcing_options ?? [])].sort((x, y) => x.rank - y.rank)[0]
    setTab('sourcing')
    setSourceSheet(preferred?.id ?? null)
    if (!preferred) tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }


  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      {/* Header */}
      <header>
        <BackButton fallback={{ to: '/manufacturers' }} fallbackLabel="Back to Manufacturers" />
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight">{mfr.name}</h1>
          <div className="flex flex-wrap items-center gap-1.5">
            {mfr.status && (
              <Badge variant={mfr.status === 'preferred' ? 'default' : 'secondary'}>
                {formatStatus(mfr.status)}
              </Badge>
            )}
            {hasShopify && <Badge variant="default">Shopify</Badge>}
            {mfr.free_shipping && <Badge variant="outline">Free shipping</Badge>}
          </div>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {mfr.url && (
            // eslint-disable-next-line no-restricted-syntax -- external URL
            <a
              href={websiteHref(mfr.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Website
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {mfr.phone && (
            <span className="inline-flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">Phone</span>
              <PhoneNumber phone={mfr.phone} />
            </span>
          )}
          <span className="inline-flex min-w-0 items-center gap-1.5">
            {address ? (
              <>
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">Address</span>
                {address}
              </>
            ) : (
              'No address on file'
            )}
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0"
              aria-label="Edit address"
              onClick={() => setAddressOpen(true)}
            >
              <Pencil className="size-3.5" aria-hidden="true" />
            </Button>
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1">
          <QualityTierSummary
            manufacturerUuid={manuId}
            onOpen={() => {
              setTab('quality-tiers')
              tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
          />
        </div>
      </header>

      <AddressSheet
        manufacturerUuid={manuId}
        manufacturerName={mfr.name}
        address={mfr}
        open={addressOpen}
        onOpenChange={setAddressOpen}
      />

      <ManufacturerGlance mfr={mfr} onOpenTerms={openPreferredSource} />

      {/* Tabbed content */}
      <Tabs
        ref={tabsRef}
        value={tab ?? (mfr.safeware ? 'protection-plans' : 'sourcing')}
        onValueChange={setTab}
        className="scroll-mt-20 pt-2"
      >
        <TabsList variant="line" className="w-full justify-start overflow-x-auto">
          {mfr.safeware && (
            <TabsTrigger value="protection-plans">
              <ShieldPlus className="h-4 w-4" />
              Protection plans
            </TabsTrigger>
          )}
          <TabsTrigger value="sourcing">
            <Truck className="h-4 w-4" />
            Sourcing
          </TabsTrigger>
          <TabsTrigger value="products">
            <Package className="h-4 w-4" />
            Products
          </TabsTrigger>
          <TabsTrigger value="quality-tiers">
            <Award className="h-4 w-4" />
            Ratings
          </TabsTrigger>
          <TabsTrigger value="map-policy">
            <ShieldCheck className="h-4 w-4" />
            MAP Policy
            {mfr.map_policy ? (
              <Badge
                variant={mfr.map_policy.status === 'active' ? 'default' : 'secondary'}
                className="ml-1 px-1.5 py-0 text-[10px]"
              >
                {mfr.map_policy.status}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="competitor-prices">
            <TrendingDown className="h-4 w-4" />
            Competitor prices
          </TabsTrigger>
          <TabsTrigger value="ap-invoices">
            <FileText className="h-4 w-4" />
            AP Invoices ({relatedApInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="emails">
            <Mail className="h-4 w-4" />
            Emails
          </TabsTrigger>
          <TabsTrigger value="calls">
            <Phone className="h-4 w-4" />
            Calls
          </TabsTrigger>
          {hasShopify && (
            <TabsTrigger value="shopify">
              <ShoppingCart className="h-4 w-4" />
              Shopify
            </TabsTrigger>
          )}
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sourcing" className="mt-3">
          <SourcingPanel
            manufacturerUuid={manuId}
            manufacturerName={mfr.name}
            options={mfr.sourcing_options ?? []}
            contactLinks={contactLinks}
            poRecipientEmail={mfr.po_recipient_email}
            defaultFreightMarkup={mfr.default_freight_booking_markup_percent}
            discounts={mfr.discounts ?? []}
            freightRules={mfr.freight_rules ?? []}
            feeRules={mfr.fee_rules ?? []}
            caseTerms={mfr.case_terms}
            paymentMethod={mfr.payment_method}
            shipsToResidential={mfr.ships_to_residential}
            specialPrograms={mfr.special_programs ?? []}
            openId={sourceSheet}
            onOpenIdChange={setSourceSheet}
          />
        </TabsContent>

        <TabsContent value="emails" className="mt-3">
          <ManufacturerEmailsTab
            manufacturerUuid={manuId}
            messageId={messageId}
            threadUuid={threadUuid}
            actionUuid={actionUuid}
            onChangeCheck={openPreferredSource}
          />
        </TabsContent>

        <TabsContent value="calls" className="mt-3">
          <CallsPanel params={{ manufacturer_uuid: manuId }} />
        </TabsContent>

        {mfr.safeware && (
          <TabsContent value="protection-plans" className="mt-3">
            <SafewarePlansPanel />
          </TabsContent>
        )}


        <TabsContent value="quality-tiers" className="mt-3">
          <QualityTiersPanel manufacturerUuid={manuId} manufacturerName={mfr.name} />
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <SearchInput
                placeholder="Search products..."
                aria-label="Search products"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value)
                  setProductsPage(1)
                }}
                className="pl-9"
              />
            </div>
            {definesProducts && (
              <Button variant="outline" onClick={() => setEditingProduct('new')}>
                <Plus className="h-4 w-4" />
                Add product
              </Button>
            )}
          </div>

          {productsLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : productsData?.items?.length ? (
            <>
              <ul className="grid grid-cols-1 overflow-hidden rounded-xl border bg-card lg:grid-cols-2">
                {productsData.items.map((product) => {
                  const imgUrl = getImageUrl(product.images)
                  const brand = product.brand?.name && product.brand.name !== mfr.name ? product.brand.name : null
                  // A product we define shows its name and has no catalog price (USA-1108).
                  const ours = product.source === 'manual'
                  const detail = ours
                    ? product.store_title
                    : [brand, product.specifications].filter(Boolean).join(' · ')
                  return (
                    <li key={product.uuid} className="flex items-center border-b lg:odd:border-r">
                      <Link
                        to="/products/$productId"
                        params={{ productId: product.uuid }}
                        className="group flex min-w-0 flex-1 items-center gap-3 px-3 py-2 transition-colors hover:bg-muted/50"
                      >
                        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt=""
                              loading="lazy"
                              className="size-full object-contain p-0.5"
                            />
                          ) : (
                            <span className="text-lg font-bold text-muted-foreground/30">
                              {product.model?.charAt(0) || '?'}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-semibold group-hover:text-primary">
                              {product.model}
                            </span>
                            {product.shopify_product_id && (
                              <Badge variant="default" className="px-1.5 py-0 text-[10px]">
                                Shopify
                              </Badge>
                            )}
                            {product.discontinued_at && (
                              <Badge variant="destructive" className="px-1.5 py-0 text-[10px]">
                                Disc.
                              </Badge>
                            )}
                          </div>
                          {detail && <p className="truncate text-xs text-muted-foreground">{detail}</p>}
                        </div>
                        {!ours && (
                          <div className="shrink-0 text-right tabular-nums">
                            <p className="text-sm font-medium">
                              {formatMoney(product.current_price?.store_price)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Net {formatMoney(product.current_price?.net_price)}
                            </p>
                          </div>
                        )}
                      </Link>
                      {ours && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="mr-2 shrink-0"
                          aria-label={`Edit ${product.model}`}
                          onClick={() => setEditingProduct(product)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Page {productsData.page} of {productsData.pages} ({productsData.total} products)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProductsPage((p) => Math.max(1, p - 1))}
                    disabled={productsPage <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="max-sm:sr-only">Previous</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProductsPage((p) => p + 1)}
                    disabled={productsPage >= (productsData.pages ?? 1)}
                  >
                    <span className="max-sm:sr-only">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <p className="py-12 text-center text-muted-foreground">
              {productSearch
                ? 'No products match your search'
                : 'No products found for this manufacturer'}
            </p>
          )}
        </TabsContent>

        {/* MAP Policy Tab */}
        <TabsContent value="map-policy" className="mt-4 space-y-4">
          <MapPolicyTab manuUuid={manuId} policy={mfr.map_policy ?? null} />
        </TabsContent>

        <TabsContent value="competitor-prices" className="mt-4">
          <ManufacturerCompetitorPrices manufacturerId={manuId} />
        </TabsContent>

        {/* Related AP invoices */}
        <RelatedApInvoicesPanel invoices={relatedApInvoices} />

        {/* Shopify Tab */}
        {hasShopify && (
          <TabsContent value="shopify" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  Shopify Collection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Collection ID</dt>
                      <dd className="font-mono text-xs">{mfr.shopify_collection_id}</dd>
                    </div>
                    {mfr.shopify_collection_slug && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Slug</dt>
                        <dd className="font-medium">{mfr.shopify_collection_slug}</dd>
                      </div>
                    )}
                    {mfr.shopify_published_at && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Published</dt>
                        <dd>{parseUTCDate(mfr.shopify_published_at).toLocaleString()}</dd>
                      </div>
                    )}
                    {mfr.shopify_hidden_at && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Hidden</dt>
                        <dd>{parseUTCDate(mfr.shopify_hidden_at).toLocaleString()}</dd>
                      </div>
                    )}
                    {mfr.shopify_created_at && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Created</dt>
                        <dd>{parseUTCDate(mfr.shopify_created_at).toLocaleString()}</dd>
                      </div>
                    )}
                    {mfr.shopify_updated_at && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Updated</dt>
                        <dd>{parseUTCDate(mfr.shopify_updated_at).toLocaleString()}</dd>
                      </div>
                    )}
                  </dl>
                  {mfr.shopify_collection_image_url && (
                    <div>
                      <img
                        src={mfr.shopify_collection_image_url}
                        alt={`${mfr.name} collection`}
                        className="rounded-lg border object-contain max-h-48"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="settings" className="mt-3 space-y-4">
          <TrackingPanel
            manufacturerUuid={manuId}
            manufacturerName={mfr.name}
            quoteTracking={mfr.quote_tracking ?? false}
            storefrontTracking={mfr.aq_tracking === true}
            customPricing={mfr.custom_pricing ?? false}
            fromAutoQuotes={mfr.source === 'auto_quotes_api'}
            aliases={mfr.aliases ?? []}
          />
          <ManufacturerDetails mfr={mfr} aliases={aliases} />
          <StoreVisibilityPanel
            manufacturerUuid={manuId}
            manufacturerName={mfr.name}
            hiddenAt={mfr.shopify_hidden_at ?? null}
            pendingProductCount={mfr.store_pending_product_count}
          />
          <RetirePanel
            manufacturerUuid={manuId}
            manufacturerName={mfr.name}
            retirement={mfr.retirement ?? null}
            activeProductCount={mfr.active_product_count}
          />
        </TabsContent>
      </Tabs>
      {definesProducts && (
        <ManualProductDialog
          manufacturerUuid={manuId}
          product={editingProduct === 'new' ? null : editingProduct}
          open={editingProduct !== null}
          onOpenChange={(open) => !open && setEditingProduct(null)}
        />
      )}
    </div>
  )
}
